<template>
  <div class="page-wrap">

    <!-- Connecting overlay -->
    <transition name="fade">
      <div class="processing-overlay" v-if="loading">
        <div class="processing-card">
          <i class="fa fa-circle-o-notch fa-spin"></i>
          <div class="processing-title">Connecting to QuickBooks…</div>
          <div class="processing-sub">Please wait while we complete the OAuth flow</div>
        </div>
      </div>
    </transition>

    <div class="panel-card">

      <div class="panel-header">
        <div class="panel-header-left">
          <div class="panel-icon"><i class="fa fa-calculator"></i></div>
          <div>
            <div class="panel-title">QuickBooks Setup</div>
            <div class="panel-sub">Connect your QuickBooks account for accounting integration</div>
          </div>
        </div>
      </div>

      <div class="panel-body">

        <!-- Enable toggle -->
        <div class="enable-row">
          <div class="enable-info">
            <div class="enable-label">Enable QuickBooks Integration</div>
            <div class="enable-sub">Allow this account to sync invoices and payments with QuickBooks</div>
          </div>
          <button
            type="button"
            class="toggle-btn"
            :class="{ on: details.is_quickbooks_enabled }"
            :title="details.is_quickbooks_enabled ? 'Disable QuickBooks' : 'Enable QuickBooks'"
            @click="details.is_quickbooks_enabled = !details.is_quickbooks_enabled; toggleQuickBooks()"
          >
            <span class="toggle-knob"></span>
          </button>
        </div>

        <transition name="slide">
          <div v-if="details.is_quickbooks_enabled" class="credentials-section">

            <div class="section-divider"><span>OAuth Credentials</span></div>

            <div class="form-group">
              <label>Client ID <span class="req">*</span></label>
              <input type="text" class="field" v-model="details.clientId"
                :class="{ 'field-err': errors['client_id'] }"
                placeholder="Enter your QuickBooks Client ID" />
              <span v-if="errors['client_id']" class="err-msg">{{ errors["client_id"][0] }}</span>
            </div>

            <div class="form-group">
              <label>Secret Key <span class="req">*</span></label>
              <input type="text" class="field" v-model="details.clientSecret"
                :class="{ 'field-err': errors['client_secret_key'] }"
                placeholder="Enter your QuickBooks Client Secret" />
              <span v-if="errors['client_secret_key']" class="err-msg">{{ errors["client_secret_key"][0] }}</span>
            </div>

            <div class="form-group">
              <label>Environment <span class="req">*</span></label>
              <select class="field field-select" v-model="details.environment"
                :class="{ 'field-err': errors['environment'] }">
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </select>
              <span v-if="errors['environment']" class="err-msg">{{ errors["environment"][0] }}</span>
            </div>

            <div class="env-hint" :class="details.environment === 'production' ? 'hint-warn' : 'hint-info'">
              <i :class="details.environment === 'production' ? 'fa fa-exclamation-triangle' : 'fa fa-info-circle'"></i>
              <span v-if="details.environment === 'production'">
                <strong>Production mode</strong> — transactions will affect your live QuickBooks data.
              </span>
              <span v-else>
                <strong>Sandbox mode</strong> — safe for testing, no real data will be modified.
              </span>
            </div>

            <div class="redirect-row">
              <span class="redirect-label">Redirect URI</span>
              <span class="redirect-value">{{ details.redirectUri }}</span>
            </div>

            <div class="form-footer">
              <button class="btn-connect" @click="onConnect">
                <i class="fa fa-plug"></i> Connect to QuickBooks
              </button>
            </div>

          </div>
        </transition>

      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  FETCH_QUICKBOOK_DATA,
  UPDATE_QUICKBOOK_CREDENTIAL,
  ENABLE_DISABLE_QUICKBOOKS
} from "../../../../core/services/store/actions.type";
import { mapGetters } from "vuex";

const OAuthClient = require('intuit-oauth');

export default {
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Setting",   route: "" },
        { id: 3, title: "QuickBooks", route: "" }
      ],
      details: {
        clientId: '',
        clientSecret: '',
        environment: 'sandbox',
        redirectUri: '',
        is_quickbooks_enabled: false
      },
      errors: [],
      loading: false
    };
  },
  computed: { ...mapGetters(["currentUser"]) },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    const client_slug = this.currentUser.u.roles === "admin"
      ? 'admin'
      : this.$route.params.client_slug;
    this.details.redirectUri = window.location.origin + '/' + client_slug + '/quickbooks-setup';
  },
  mounted() {
    const data = JSON.parse(window.localStorage.getItem('quickbooks_details'));
    if (data) {
      this.details.clientId             = data.clientId;
      this.details.clientSecret         = data.clientSecret;
      this.details.environment          = data.environment;
      this.details.is_quickbooks_enabled = data.is_quickbooks_enabled;

      const code    = this.$route.query.code;
      const state   = this.$route.query.state;
      const realmId = this.$route.query.realmId;

      if (code && state && realmId) {
        this.onDataSet(code, state, realmId);
        const url = new URL(window.location.href);
        url.search = '';
        window.history.replaceState({}, '', url.toString());
      } else {
        window.localStorage.removeItem('quickbooks_details');
        this.fetchData();
      }
    } else {
      this.fetchData();
    }
  },
  methods: {
    toggleQuickBooks() {
      this.$store.dispatch(ENABLE_DISABLE_QUICKBOOKS, {
        is_quickbooks_enabled: this.details.is_quickbooks_enabled
      }).then(() => {
        if (!this.details.is_quickbooks_enabled) {
          window.localStorage.removeItem('quickbooks_details');
        } else {
          setTimeout(() => this.fetchData(), 500);
        }
      }).catch(err => {
        this.$toastr.e(err.msg);
      });
    },
    onConnect() {
      this.errors = {};
      const errs = {};
      if (!this.details.clientId)     errs.client_id          = ["Client ID is required"];
      if (!this.details.clientSecret) errs.client_secret_key  = ["Secret Key is required"];
      if (!this.details.environment)  errs.environment        = ["Environment is required"];
      if (Object.keys(errs).length) { this.errors = errs; return; }

      window.localStorage.setItem('quickbooks_details', JSON.stringify(this.details));
      const oauthClient = new OAuthClient(this.details);
      const authUri = oauthClient.authorizeUri({
        scope: [OAuthClient.scopes.Accounting, OAuthClient.scopes.OpenId],
        state: 'testState'
      });
      window.location.href = authUri;
    },
    onDataSet(code, state, realmId) {
      this.loading = true;
      window.localStorage.removeItem('quickbooks_details');
      this.$store.dispatch(UPDATE_QUICKBOOK_CREDENTIAL, {
        client_secret_key: this.details.clientSecret,
        client_id:         this.details.clientId,
        realm_id:          realmId,
        auth_code:         code,
        state:             state,
        environment:       this.details.environment,
        redirect_uri:      this.details.redirectUri
      }).then(() => {
        this.loading = false;
        this.$toastr.s("Connected Successfully!");
      }).catch(err => {
        this.loading = false;
        this.$toastr.e((err && (err.error || err.msg)) || "Invalid credentials — please check your Client ID and Secret.");
      });
    },
    fetchData() {
      this.$store.dispatch(FETCH_QUICKBOOK_DATA).then(data => {
        this.details.clientId              = data.quickbook_client_id    || '';
        this.details.clientSecret          = data.quickbook_client_secret || '';
        this.details.environment           = data.environment            || 'sandbox';
        this.details.is_quickbooks_enabled = data.is_quickbooks_enabled;
      }).catch(() => {});
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$green:  #276749;
$orange: #F58020;
$dark:   #0d1b3e;
$border: #dde2ec;

.page-wrap {
  padding: 24px;
  font-family: $font;
  max-width: 580px;
}

/* Processing overlay */
.processing-overlay {
  position: fixed; inset: 0; z-index: 1050;
  background: rgba(13,27,62,0.45);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
}
.processing-card {
  background: #fff; border-radius: 16px;
  padding: 36px 44px; text-align: center;
  box-shadow: 0 8px 32px rgba(13,27,62,0.18);
  i { font-size: 32px; color: $blue; margin-bottom: 14px; display: block; }
}
.processing-title { font-size: 15px; font-weight: 700; color: $dark; margin-bottom: 6px; }
.processing-sub   { font-size: 12px; color: #8a94a6; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* Panel */
.panel-card {
  background: #fff; border-radius: 16px;
  box-shadow: 0 2px 16px rgba(13,27,62,0.08);
  overflow: hidden;
}
.panel-header {
  display: flex; align-items: center;
  padding: 20px 24px 18px;
  border-bottom: 1px solid #f0f3fa;
  background: #fafbff;
}
.panel-header-left { display: flex; align-items: center; gap: 14px; }
.panel-icon {
  width: 40px; height: 40px; border-radius: 10px;
  background: linear-gradient(135deg, $blue, #2176ae);
  color: #fff; display: flex; align-items: center; justify-content: center;
  font-size: 17px; flex-shrink: 0;
}
.panel-title { font-size: 15px; font-weight: 700; color: $dark; }
.panel-sub   { font-size: 12px; color: #8a94a6; margin-top: 2px; }

.panel-body { padding: 24px; }

/* Enable toggle row */
.enable-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 18px;
  background: #f7f9fc;
  border: 1.5px solid $border; border-radius: 12px;
  margin-bottom: 4px;
}
.enable-info { flex: 1; }
.enable-label { font-size: 14px; font-weight: 600; color: $dark; }
.enable-sub   { font-size: 12px; color: #8a94a6; margin-top: 3px; }

.toggle-btn {
  position: relative; width: 44px; height: 24px;
  border-radius: 12px; border: none; cursor: pointer;
  background: #d1d5db; flex-shrink: 0; transition: background 0.2s;
  &.on { background: $blue; }
}
.toggle-knob {
  position: absolute; top: 3px; left: 3px;
  width: 18px; height: 18px; border-radius: 50%;
  background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: transform 0.2s;
  .toggle-btn.on & { transform: translateX(20px); }
}

/* Slide transition */
.slide-enter-active { transition: all 0.22s ease; }
.slide-leave-active { transition: all 0.18s ease; }
.slide-enter, .slide-leave-to { opacity: 0; transform: translateY(-8px); }

/* Credentials section */
.credentials-section { margin-top: 20px; }

.section-divider {
  display: flex; align-items: center; gap: 10px; margin-bottom: 20px;
  span {
    font-size: 11px; font-weight: 700; color: #8a94a6;
    text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap;
  }
  &::before, &::after { content: ''; flex: 1; height: 1px; background: #f0f3fa; }
}

/* Form */
.form-group { margin-bottom: 18px; }

label {
  display: block; font-size: 11px; font-weight: 700;
  color: #4a5568; text-transform: uppercase; letter-spacing: 0.4px;
  margin-bottom: 6px;
}
.req { color: #e53e3e; }

.field {
  display: block; width: 100%; height: 38px;
  border: 1.5px solid $border; border-radius: 8px;
  padding: 0 12px; font-size: 13px; font-family: $font; color: $dark;
  background: #fff; outline: none; box-shadow: none;
  transition: border-color 0.15s;
  &:focus { border-color: $blue; }
  &.field-err { border-color: #e53e3e; }
}
.field-select {
  padding-right: 32px;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E") no-repeat right 12px center;
  appearance: none; cursor: pointer;
}
.err-msg { font-size: 11px; color: #e53e3e; margin-top: 3px; display: block; }

/* Environment hint */
.env-hint {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 10px 14px; border-radius: 8px; font-size: 12px; line-height: 1.5;
  margin-bottom: 18px;
  i { margin-top: 1px; flex-shrink: 0; }
  &.hint-info {
    background: #eef7fb; color: #2176ae; border: 1px solid #c8e6f7;
    i { color: $blue; }
  }
  &.hint-warn {
    background: #fff8ec; color: #92400e; border: 1px solid #fcd9a0;
    i { color: $orange; }
  }
}

/* Redirect URI */
.redirect-row {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px;
  background: #f7f9fc; border: 1.5px solid $border; border-radius: 8px;
  margin-bottom: 24px;
}
.redirect-label {
  font-size: 11px; font-weight: 700; color: #8a94a6;
  text-transform: uppercase; letter-spacing: 0.4px; flex-shrink: 0;
}
.redirect-value {
  font-size: 12px; color: $dark; font-family: 'Courier New', monospace;
  word-break: break-all;
}

/* Footer */
.form-footer {
  display: flex; justify-content: flex-end;
  padding-top: 4px;
}
.btn-connect {
  display: inline-flex; align-items: center; gap: 8px;
  height: 40px; padding: 0 28px; border-radius: 10px;
  border: none; background: $green; color: #fff;
  font-size: 13px; font-weight: 600; font-family: $font;
  cursor: pointer; transition: background 0.15s;
  i { font-size: 13px; }
  &:hover { background: #1d5230; }
}
</style>
